import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { RichTextEditor } from './RichTextEditor';
interface AddInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (invoice: {
    supplier: string;
    invoiceNumber: string;
    date: string;
    category: 'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet';
    type: 'Ladeboks' | 'Andet';
    amount: number;
    paid: boolean;
    customerId?: string;
    customerName?: string;
    notes?: string;
  }) => void;
  customers: Array<{
    id: string;
    customerId: string;
    name: string;
  }>;
}
export function AddInvoiceModal({
  isOpen,
  onClose,
  onAdd,
  customers
}: AddInvoiceModalProps) {
  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState<'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet'>('Materialer');
  const [type, setType] = useState<'Ladeboks' | 'Andet'>('Ladeboks');
  const [amount, setAmount] = useState('');
  const [paid, setPaid] = useState('no');
  const [customerId, setCustomerId] = useState('');
  const [notes, setNotes] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplier.trim() || !invoiceNumber.trim() || !date || !amount) return;
    const customer = customers.find(c => c.id === customerId);
    onAdd({
      supplier,
      invoiceNumber,
      date,
      category,
      type,
      amount: parseFloat(amount),
      paid: paid === 'yes',
      customerId: customerId || undefined,
      customerName: customer?.name || undefined,
      notes: notes || undefined
    });
    // Reset form
    setSupplier('');
    setInvoiceNumber('');
    setDate('');
    setCategory('Materialer');
    setType('Ladeboks');
    setAmount('');
    setPaid('no');
    setCustomerId('');
    setNotes('');
    onClose();
  };
  return <AnimatePresence data-id="element-181">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-182" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-183">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 my-8" data-id="element-184">
              <div className="flex items-center justify-between mb-6" data-id="element-185">
                <h2 className="text-2xl font-semibold text-stone-900" data-id="element-186">
                  Tilføj faktura
                </h2>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-187">
                  <XIcon className="w-6 h-6" data-id="element-188" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-189">
                <div data-id="element-190">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-191">
                    Faktura information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-192">
                    <Input label="Leverandør" value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="Indtast leverandør" required data-id="element-193" />

                    <Input label="Faktura nr." value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="f.eks. INV-2024-001" required data-id="element-194" />

                    <Input label="Dato" type="date" value={date} onChange={e => setDate(e.target.value)} required data-id="element-195" />

                    <Select label="Type" value={type} onChange={e => setType(e.target.value as 'Ladeboks' | 'Andet')} options={[{
                  value: 'Ladeboks',
                  label: 'Ladeboks'
                }, {
                  value: 'Andet',
                  label: 'Andet'
                }]} required data-id="element-196" />

                    <Select label="Kategori" value={category} onChange={e => setCategory(e.target.value as 'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet')} options={[{
                  value: 'Sikkerhed',
                  label: 'Sikkerhed'
                }, {
                  value: 'Værktøj',
                  label: 'Værktøj'
                }, {
                  value: 'Materialer',
                  label: 'Materialer'
                }, {
                  value: 'Andet',
                  label: 'Andet'
                }]} required data-id="element-197" />

                    <Input label="Beløb (kr)" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="f.eks. 1500" required data-id="element-198" />

                    <Select label="Betalt" value={paid} onChange={e => setPaid(e.target.value)} options={[{
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'yes',
                  label: 'Ja'
                }]} data-id="element-199" />

                    <Select label="Kunde (valgfri)" value={customerId} onChange={e => setCustomerId(e.target.value)} options={[{
                  value: '',
                  label: 'Ingen kunde'
                }, ...customers.map(c => ({
                  value: c.id,
                  label: `${c.name} (${c.customerId})`
                }))]} data-id="element-200" />
                  </div>

                  <div className="mt-4" data-id="element-201">
                    <RichTextEditor label="Noter" value={notes} onChange={setNotes} placeholder="Tilføj noter om denne faktura" rows={3} data-id="element-202" />
                  </div>
                </div>

                <div className="flex gap-3 pt-2" data-id="element-203">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-204">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-205">
                    Tilføj faktura
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}